import { useState, useEffect } from "react"
import Input from './Input';
import Select from "./Select";
import Button from "./Button";
import { Trash2, Plus } from "lucide-react";
import { useCreateUser, useUpdateUser } from "../Use-auth";

const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
        age--;
    }

    return age;
};

function CreateTeam({
    departments = [],
    defaultDepartment = "",
    mode = "create",
    initialData = null,
    onClose,
}) {
    const { mutate, isPending: isLoading } = useCreateUser();
    const { mutate: updateUser, isPending: isUpdating } = useUpdateUser();
    const [image, setImage] = useState(null);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [isSalarySet, setIsSalarySet] = useState(false);

    const loading = isLoading || isUpdating;

    const formatDate = (date) =>
        date ? new Date(date).toISOString().split("T")[0] : "";

    useEffect(() => {
        if (defaultDepartment) {
            setEmployee((prev) => ({
                ...prev,
                department: defaultDepartment,
            }));
        }
    }, [defaultDepartment]);

    useEffect(() => {
        if (mode !== "edit" || !initialData) return;

        const workInfo = initialData.workInformation || {};
        const personalInfo = initialData.personalDetails || {};
        const latestPayroll = initialData.payroll?.length
            ? [...initialData.payroll].sort(
                (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
            )[0]
            : null;

        setEmployee({
            employeeId: initialData.employeeId || "",
            firstName: workInfo.firstName || "",
            lastName: workInfo.lastName || "",
            officeEmail: initialData.officeEmail || "",
            department: initialData.department || defaultDepartment || "",
            role: initialData.role || "",
            status: initialData.employeeStatus || "",
            terminationDate: formatDate(workInfo.terminationDate),
            designation: workInfo.designation || "",
            sourceOfHire: workInfo.sourceOfHire || "",
            joiningDate: formatDate(workInfo.joiningDate),
            totalExperience: workInfo.joiningDate
                ? calculateExperience(workInfo.joiningDate)
                : workInfo.totalExperience || "",
            dob: personalInfo.dob?.slice(0, 10) || "",
            age: personalInfo.age || "",
            gender: personalInfo.gender || "",
            maritalStatus: personalInfo.maritalStatus || "",
            mobile: personalInfo.mobile || "",
            guardianmobile: personalInfo.guardianmobile || "",
            personalEmail: personalInfo.personalEmail || "",
            address: personalInfo.address || "",
            addressLine: personalInfo.addressLine || "",
            permanentLine: personalInfo.permanentLine || "",
            aadhaar: personalInfo.aadhaar || "",
            pan: personalInfo.pan || "",
            guardianName: personalInfo.guardianName || "",
            relationship: personalInfo.relationship || "",
            guardianContact: personalInfo.guardianContact || "",

            joiningSalary: workInfo.joiningSalary || "",
            currentSalary: latestPayroll?.annualCtc || "",
            resignationDate: formatDate(workInfo.resignationDate),
            employeeShift: workInfo.employeeShift || "",
            bankName: workInfo.bankName || "",
            bankAccountNumber: workInfo.bankAccountNumber || "",
            iFSCcode: workInfo.iFSCcode || "",
            trainingStartDate: formatDate(workInfo.trainingStartDate),
            trainingEndDate: formatDate(workInfo.trainingEndDate),
            trainingPeriod: workInfo.trainingPeriod || "",
        });
        setIsSalarySet(!!latestPayroll?.annualCtc);
        setWorkExp(
            initialData.workExperience?.map((item) => ({
                ...item,
                from: formatDate(item.from),
                to: formatDate(item.to),
            })) || [{ company: "", title: "", from: "", to: "", description: "" }]
        );

        setEducation(
            initialData.educationDetails?.map((item) => ({
                ...item,
                completionDate: formatDate(item.completionDate),
            })) || [{ institute: "", degree: "", specialization: "", completionDate: "" }]
        );
    }, [mode, initialData, defaultDepartment]);

    const [employee, setEmployee] = useState({
        employeeId: "",
        firstName: "",
        lastName: "",
        officeEmail: "",
        department: "",
        role: "",
        status: "Active",
        designation: "",
        sourceOfHire: "",
        joiningDate: "",
        totalExperience: "",

        joiningSalary: "",
        currentSalary: "",
        resignationDate: "",
        employeeShift: "",
        bankName: "",
        bankAccountNumber: "",
        iFSCcode: "",
        trainingStartDate: "",
        trainingEndDate: "",
        trainingPeriod: "",
        terminationDate: "",

        dob: "",
        age: "",
        gender: "",
        maritalStatus: "",
        mobile: "",
        guardianmobile: "",
        personalEmail: "",
        address: "",
        addressLine: "",
        permanentLine: "",
        aadhaar: "",
        pan: "",
        guardianName: "",
        relationship: "",
        guardianContact: "",
    })

    const [workExp, setWorkExp] = useState([
        { company: "", title: "", from: "", to: "", description: "" },
    ])

    const [education, setEducation] = useState([
        { institute: "", degree: "", specialization: "", completionDate: "" },
    ])

    const resetForm = () => {
        setEmployee({
            employeeId: "",
            firstName: "",
            lastName: "",
            officeEmail: "",
            department: "",
            role: "",
            status: "",
            designation: "",
            sourceOfHire: "",
            joiningDate: "",
            totalExperience: "",

            joiningSalary: "",
            currentSalary: "",
            eesignationDate: "",
            employeeShift: "",
            bankName: "",
            bankAccountNumber: "",
            iFSCcode: "",
            trainingStartDate: "",
            trainingEndDate: "",
            trainingPeriod: "",
            terminationDate: "",

            dob: "",
            age: "",
            gender: "",
            maritalStatus: "",
            mobile: "",
            guardianmobile: "",
            personalEmail: "",
            address: "",
            addressLine: "",
            permanentLine: "",
            aadhaar: "",
            pan: "",
            guardianName: "",
            relationship: "",
            guardianContact: "",
        });

        setWorkExp([{ company: "", title: "", from: "", to: "", description: "" }]);
        setEducation([{ institute: "", degree: "", specialization: "", completion: "" }]);
        setImage(null);
        setErrorMsg("");
    };

    const handleEmployeeChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === "dob") {
            const age = calculateAge(value);

            setEmployee((prev) => ({
                ...prev,
                dob: value,
                age: age,
            }));

            return;
        }

        if (name === "joiningDate") {
            const experience = calculateExperience(value);
            setEmployee((prev) => ({
                ...prev,
                joiningDate: value,
                totalExperience: experience,
            }));
            return;
        }

        if (name === "firstName" || name === "lastName"
            || name === "sourceOfHire" || name === "guardianName") {
            newValue = value.replace(/[^a-zA-Z\s]/g, '');
        }

        else if (["age", "mobile", "guardianmobile", "aadhaar", "guardianContact"].includes(name)) {
            newValue = value.replace(/[^0-9\s]/g, '');
        }
        else if (["employeeId", "pan"].includes(name)) {
            newValue = value.replace(/[^a-zA-Z0-9\s]/g, '');
        }
        else if (name === "officeEmail" || name === "personalEmail") {
            newValue = value;
        }
        setEmployee((prev) => ({ ...prev, [name]: newValue }));
    };

    const handleWorkExpChange = (index, e) => {
        const { name, value } = e.target;
        const data = [...workExp];

        let newValue = value;

        if (name === "company" || name === "title") {
            newValue = value.replace(/[^a-zA-Z0-9\s.]/g, '');
        } else if (name === "description") {
            newValue = value.replace(/[^a-zA-Z\s]/g, '');
        }
        data[index][name] = newValue;

        setWorkExp(data);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        setErrorMsg("");
        setSuccessMsg("");

        const formData = new FormData();

        formData.append("employeeId", employee.employeeId);
        formData.append("officeEmail", employee.officeEmail);
        formData.append("role", employee.role);
        formData.append("department", employee.department);
        formData.append("employeeStatus", employee.status);
        formData.append("departmentName", employee.department);

        formData.append(
            "workInformation",
            JSON.stringify([
                {
                    firstName: employee.firstName,
                    lastName: employee.lastName,
                    department: employee.department,
                    designation: employee.designation,
                    sourceOfHire: employee.sourceOfHire,
                    joiningDate: employee.joiningDate || null,
                    totalExperience: employee.totalExperience,
                    joiningSalary: employee.joiningSalary,
                    currentSalary: employee.currentSalary,
                    resignationDate: employee.resignationDate || null,
                    employeeShift: employee.employeeShift,
                    bankName: employee.bankName,
                    bankAccountNumber: employee.bankAccountNumber,
                    iFSCcode: employee.iFSCcode,
                    trainingStartDate: employee.trainingStartDate || null,
                    trainingEndDate: employee.trainingEndDate || null,
                    trainingPeriod: employee.trainingPeriod,
                    terminationDate: employee.terminationDate || null
                },
            ])
        );

        formData.append(
            "personalDetails",
            JSON.stringify([
                {
                    dob: employee.dob || null,
                    age: employee.age,
                    gender: employee.gender,
                    maritalStatus: employee.maritalStatus,
                    mobile: employee.mobile,
                    guardianmobile: employee.guardianmobile,
                    personalEmail: employee.personalEmail,
                    address: employee.address,
                    addressLine: employee.addressLine,
                    permanentLine: employee.permanentLine,
                    aadhaar: employee.aadhaar,
                    pan: employee.pan,
                    guardianName: employee.guardianName,
                    relationship: employee.relationship,
                    guardianContact: employee.guardianContact,
                },
            ])
        );

        const cleanedWorkExp = workExp.map(item => ({
            ...item,
            from: item.from || null,
            to: item.to || null,
        }));

        const cleanedEducation = education.map(item => ({
            ...item,
            completionDate: item.completionDate || null,
        }));

        formData.append("workExperience", JSON.stringify(cleanedWorkExp));
        formData.append("educationDetails", JSON.stringify(cleanedEducation));

        if (image) {
            formData.append("image", image);
        }

        if (mode === "edit") {
            updateUser(
                { id: initialData.employeeId, data: formData },
                {
                    onSuccess: (data) => {
                        setErrorMsg("");
                        setSuccessMsg(data.message || "Employee updated successfully");
                        onClose();
                    },
                    onError: (error) => {
                        setSuccessMsg("");
                        setErrorMsg(error.message || "Update failed");
                    },
                }
            );
        } else {
            mutate(formData, {
                onSuccess: (data) => {
                    setErrorMsg("");
                    setSuccessMsg(data.message || "Employee created successfully");
                    resetForm();
                    onClose();
                },
                onError: (error) => {
                    setSuccessMsg("");
                    setErrorMsg(error.message || "Something went wrong");
                },
            });
        }
    };

    const addWorkExp = () => {
        setWorkExp([
            ...workExp,
            { company: "", title: "", from: "", to: "", description: "" },
        ])
    }

    const addEducation = () => {
        setEducation([
            ...education,
            { institute: "", degree: "", specialization: "", completionDate: "" },
        ])
    }
    const removeWorkExp = (index) => {
        if (workExp.length === 1) return;

        const updated = workExp.filter((_, i) => i !== index);
        setWorkExp(updated);
    };

    const removeEducation = (index) => {
        if (education.length === 1) return;

        const updated = education.filter((_, i) => i !== index);
        setEducation(updated);
    };

    const handleCancel = () => {
        if (mode === "edit" && onClose) {
            onClose();
        } else {
            resetForm();
            onClose();
        }
    };

    const calculateExperience = (joiningDate) => {
        if (!joiningDate) return "";

        const joining = new Date(joiningDate);
        const today = new Date();

        let years = today.getFullYear() - joining.getFullYear();
        let months = today.getMonth() - joining.getMonth();

        if (months < 0) {
            years--;
            months += 12;
        }

        if (years === 0) return `${months} Month${months !== 1 ? "s" : ""}`;
        if (months === 0) return `${years} Year${years !== 1 ? "s" : ""}`;
        return `${years} Year${years !== 1 ? "s" : ""} ${months} Month${months !== 1 ? "s" : ""}`;
    };

    return (
        <div className="min-h-screen shadow-md">
            <form
                onSubmit={handleSubmit}
                className="max-w-9xl mx-auto bg-white p-2 sm:p-6 rounded-lg shadow  mt-3"
            >
                {/* ================= WORK INFO ================= */}
                <h2 className="text-[14px] sm:text-sm md:text-lg font-semibold mb-4 border-b pb-2">
                    Work Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Input
                        label="Employee ID"
                        name="employeeId"
                        value={employee.employeeId}
                        onChange={handleEmployeeChange}
                        required
                        maxLength={10}
                    />

                    <div>
                        <Input
                            label="Employee Image"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                if (!file.type.startsWith("image/")) {
                                    alert("Only image files are allowed");
                                    e.target.value = "";
                                    return;
                                }

                                const maxSize = 618 * 1024;
                                if (file.size > maxSize) {
                                    alert("File size must be 618 Kb or less");
                                    e.target.value = "";
                                    return;
                                }

                                setImage(file);
                            }}
                        />
                        <small className="text-gray-600">
                            Image must be square and ≤ 618 KB
                        </small>
                    </div>
                    <Input
                        label="First Name"
                        name="firstName"
                        value={employee.firstName}
                        onChange={handleEmployeeChange}
                        required
                        maxLength={30}
                    />
                    <Input
                        label="Last Name"
                        name="lastName"
                        value={employee.lastName}
                        onChange={handleEmployeeChange}
                        maxLength={30}

                    />
                    <Input
                        label="Office Email"
                        type="email"
                        name="officeEmail"
                        value={employee.officeEmail}
                        onChange={handleEmployeeChange}
                        required
                        maxLength={70}
                    />
                    <Select
                        label="Department"
                        name="department"
                        value={employee.department}
                        onChange={handleEmployeeChange}
                        options={[
                            "Admin",
                            "HR",
                            "Management",
                            "Sales",
                            "Development",
                            "Design",
                            "Facilities"
                        ]}
                        required
                    />

                    <Select
                        label="Designation"
                        name="role"
                        value={employee.role}
                        onChange={handleEmployeeChange}
                        options={["Graphic Designer",
                            "Full Stack Developer", "QA / Test Engineer", "Project Manager", "Project Coordinator",
                            "Sales Executive", "Content Writer", "Shopify Developer", "Mern-Stack Developer", "WordPress Developer", "HR Executive",
                            "HR Manager", "Dev TL", "Design TL", "Office Boy", "Recruiter", "Payroll Executive", "Training & Development Executive",
                            "HR Operations Executive", "Chief Executive Officer (CEO)", "Sales Manager", " Business Development Manager (BMD)",
                            "Account Manager Executive", "Lead Generation Executive", "Digital Marketing Executive", "Software Developer",
                            "Web Developer", "Front end Developer", "Full Stack Developer", "E-commerce Developer", "UI/UX Designer",
                            "Motion Designer", "Web Designer", "SEO-Executive", "Sales Representative"
                        ]}
                        required
                    />

                    <Select
                        label="Employee Status"
                        name="status"
                        value={employee.status}
                        onChange={handleEmployeeChange}
                        options={["Active", "Terminated", "Resigned", "Trainee"]}
                        required
                        labelClassName="text-green-600 font-semibold"
                    />

                    {/* <Input
                        label="Designation"
                        name="designation"
                        value={employee.designation}
                        onChange={handleEmployeeChange}
                        required
                    /> */}
                    {(employee.status === "Active" || employee.status === "Trainee") && (
                        <Input
                            label="Source of Hire"
                            name="sourceOfHire"
                            value={employee.sourceOfHire}
                            onChange={handleEmployeeChange}
                            maxLength={200}
                        />
                    )}


                    {(employee.status === "Active" || employee.status === "Trainee") && (<Input
                        label="Date of Joining"
                        type="date"
                        name="joiningDate"
                        value={employee.joiningDate}
                        onChange={handleEmployeeChange}
                        max={new Date().toISOString().split("T")[0]}
                    />)}

                    {employee.status !== "Trainee" && (
                        <Input
                            label="Total Experience (Years)"
                            type="text"
                            name="totalExperience"
                            value={employee.totalExperience || ""}
                            onChange={handleEmployeeChange}
                            disabled
                        />)}
                    {/* <Input
                        label="Joining Salary"
                        type="text"
                        name="joiningSalary"
                        value={employee.joiningSalary}
                        onChange={handleEmployeeChange}
                        maxLength={50}
                    /> */}

                    <Input
                        label="Annual CTC"
                        type="text"
                        name="currentSalary"
                        value={employee.currentSalary || ""}
                        onChange={handleEmployeeChange}
                        maxLength={50}
                        disabled={isSalarySet}
                    />
                    {employee.status === "Resigned" && (
                        <Input
                            label="Resignation Date"
                            type="date"
                            name="resignationDate"
                            value={employee.resignationDate}
                            onChange={handleEmployeeChange}
                        />
                    )}

                    <Select
                        label="Employee Shift"
                        name="employeeShift"
                        value={employee.employeeShift}
                        onChange={handleEmployeeChange}
                        options={["Day Shift", "Night Shift", "Flex Time"]}
                    />

                    {employee.status === "Trainee" && (
                        <>
                            <Input
                                label="Training Start Date"
                                type="date"
                                name="trainingStartDate"
                                value={employee.trainingStartDate}
                                onChange={handleEmployeeChange}
                            />

                            <Input
                                label="Training End Date"
                                type="date"
                                name="trainingEndDate"
                                value={employee.trainingEndDate}
                                onChange={handleEmployeeChange}
                            />

                            <Select
                                label="Training Period"
                                name="trainingPeriod"
                                value={employee.trainingPeriod}
                                onChange={handleEmployeeChange}
                                options={[
                                    "1 months",
                                    "2 months",
                                    "3 months",
                                    "4 months",
                                    "5 months",
                                    "6 months",
                                ]}
                            />
                        </>
                    )}

                    {employee.status === "Terminated" && (
                        <Input
                            label="Termination Date"
                            type="date"
                            name="terminationDate"
                            value={employee.terminationDate}
                            onChange={handleEmployeeChange}
                        />
                    )}
                </div>

                {/* ================= PERSONAL DETAILS ================= */}
                <h2 className="text-[14px] sm:text-sm md:text-lg font-semibold mt-8 mb-4 border-b pb-2">
                    Bank Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Input
                        label="Bank Name"
                        type="text"
                        name="bankName"
                        value={employee.bankName}
                        onChange={handleEmployeeChange}
                        maxLength={50}
                    />
                    <Input
                        label="Bank Account Number"
                        type="text"
                        name="bankAccountNumber"
                        value={employee.bankAccountNumber}
                        onChange={handleEmployeeChange}
                        maxLength={20}
                    />
                    <Input
                        label="IFSC Code"
                        type="text"
                        name="iFSCcode"
                        value={employee.iFSCcode}
                        onChange={handleEmployeeChange}
                        maxLength={20}
                    />
                </div>
                {/* ================= PERSONAL DETAILS ================= */}
                <h2 className="text-[14px] sm:text-sm md:text-lg font-semibold mt-8 mb-4 border-b pb-2">
                    EMP Personal Details
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Input
                        label="Date of Birth"
                        type="date"
                        name="dob"
                        value={employee.dob}
                        onChange={handleEmployeeChange}
                        max={new Date().toISOString().split("T")[0]}
                    />
                    <Input
                        label="Age"
                        type="text"
                        name="age"
                        value={employee.age}
                        disabled
                    />

                    <Select
                        label="Gender"
                        name="gender"
                        value={employee.gender}
                        onChange={handleEmployeeChange}
                        options={["Male", "Female", "Other"]}
                    />

                    <Select
                        label="Marital Status"
                        name="maritalStatus"
                        value={employee.maritalStatus}
                        onChange={handleEmployeeChange}
                        options={["Single", "Married"]}
                    />

                    <Input
                        label="Personal Mobile Number"
                        name="mobile"
                        value={employee.mobile}
                        onChange={handleEmployeeChange}
                        maxLength={12}

                    />

                    {/* <Input
                        label="Guardian Mobile Number"
                        name="guardianmobile"
                        value={employee.guardianmobile}
                        onChange={handleEmployeeChange}
                        maxLength={12}
                    /> */}

                    <Input
                        label="Personal Email"
                        type="email"
                        name="personalEmail"
                        value={employee.personalEmail}
                        onChange={handleEmployeeChange}
                        maxLength={70}

                    />

                    <Input
                        label="Present Address"
                        name="address"
                        value={employee.address}
                        onChange={handleEmployeeChange}
                        maxLength={300}
                    />

                    {/* <Input
                        label="Address Line"
                        name="addressLine"
                        value={employee.addressLine}
                        onChange={handleEmployeeChange}
                        maxLength={300}
                    /> */}

                    <Input
                        label="Permanent Address"
                        name="permanentLine"
                        value={employee.permanentLine}
                        onChange={handleEmployeeChange}
                        maxLength={300}
                    />

                    <Input
                        label="Aadhaar Number"
                        name="aadhaar"
                        value={employee.aadhaar}
                        onChange={handleEmployeeChange}
                        maxLength={15}

                    />

                    <Input
                        label="PAN Number"
                        name="pan"
                        value={employee.pan}
                        onChange={handleEmployeeChange}
                        maxLength={15}

                    />
                    <Input
                        label="Guardian / Spouse Name"
                        name="guardianName"
                        value={employee.guardianName}
                        onChange={handleEmployeeChange}
                        maxLength={100}
                    />

                    <Select
                        label="Relationship"
                        name="relationship"
                        value={employee.relationship}
                        onChange={handleEmployeeChange}
                        options={["Father", "Mother", "Brother", "Sister", "Husband", "Wife"]}
                    />

                    <Input
                        label="Guardian / Spouse Contact"
                        name="guardianContact"
                        value={employee.guardianContact}
                        onChange={handleEmployeeChange}
                        maxLength={12}

                    />
                </div>

                <div className="flex items-center justify-between mt-4 mb-2 sm:mt-8 sm:mb-4 border-b pb-2">
                    <h2 className="text-[14px] sm:text-sm md:text-lg font-semibold mt-2 sm:mt-8   pb-2">Work Experience</h2>

                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            onClick={addWorkExp}
                            className="hidden sm:flex"
                        >
                            ADD MULTIPLE RECORD
                        </Button>
                        <button
                            type="button"
                            onClick={addWorkExp}
                            className="flex sm:hidden items-center justify-center p-2 rounded bg-gray-100 hover:bg-gray-200"
                        >
                            <Plus size={18} color="orange" />
                        </button>
                    </div>
                </div>

                {workExp.map((item, index) => (

                    <div
                        key={index}
                        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 mb-4 items-end"
                    >
                        <Input
                            label="Company Name"
                            name="company"
                            value={item.company}
                            onChange={(e) => handleWorkExpChange(index, e)}
                            maxLength={200}
                        />

                        <Input
                            label="Job Title"
                            name="title"
                            value={item.title}
                            onChange={(e) => handleWorkExpChange(index, e)}
                            maxLength={200}
                        />

                        <Input
                            label="From"
                            name="from"
                            type="date"
                            value={item.from}
                            onChange={(e) => handleWorkExpChange(index, e)}
                            max={new Date().toISOString().split("T")[0]}
                        />

                        <Input
                            label="To"
                            name="to"
                            type="date"
                            value={item.to}
                            onChange={(e) => handleWorkExpChange(index, e)}
                            max={new Date().toISOString().split("T")[0]}
                        />

                        <Input
                            label="Job Description"
                            name="description"
                            value={item.description}
                            onChange={(e) => handleWorkExpChange(index, e)}
                            maxLength={300}
                        />

                        {workExp.length > 1 && (
                            <div className="flex justify-end sm:justify-center md:justify-center">
                                <button
                                    type="button"
                                    onClick={() => removeWorkExp(index)}
                                    className="p-2 rounded hover:bg-red-100"
                                >
                                    <Trash2 size={16} className="text-red-500" />
                                </button>
                            </div>
                        )}
                    </div>

                ))}

                <div className="flex items-center justify-between mt-4 mb-2 sm:mt-8 sm:mb-4 border-b pb-2">
                    <h2 className="text-[14px] sm:text-sm md:text-lg font-semibold mt-2 sm:mt-8  pb-2">Education Details</h2>

                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            onClick={addEducation}
                            className="hidden sm:flex"
                        >
                            ADD MULTIPLE RECORD
                        </Button>
                        <button
                            type="button"
                            onClick={addEducation}
                            className="flex sm:hidden items-center justify-center p-2 rounded bg-gray-100 hover:bg-gray-200"
                        >
                            <Plus size={18} color="orange" />
                        </button>
                    </div>
                </div>

                {education.map((item, index) => (
                    <div
                        key={index}
                        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-4 items-end"
                    >
                        <Input
                            label="Institute Name"
                            name="institute"
                            value={item.institute}
                            maxLength={200}
                            onChange={(e) => {
                                const data = [...education];
                                data[index] = {
                                    ...data[index],
                                    institute: e.target.value.replace(/[^a-zA-Z0-9\s.,/+()\-]/g, "")
                                };

                                setEducation(data);
                            }}
                        />

                        <Input
                            label="Degree / Diploma"
                            name="degree"
                            value={item.degree}
                            maxLength={200}
                            onChange={(e) => {
                                const data = [...education];
                                data[index].degree = e.target.value.replace(/[^a-zA-Z0-9\s.,/+()\-]/g, "")
                                setEducation(data);
                            }}
                        />

                        <Input
                            label="Specialization"
                            name="specialization"
                            value={item.specialization}
                            maxLength={300}
                            onChange={(e) => {
                                const data = [...education];
                                data[index].specialization = e.target.value.replace(/[^a-zA-Z0-9\s.,/-]/g, "");
                                setEducation(data);
                            }}
                        />

                        <Input
                            label="Completion Date"
                            type="date"
                            name="completionDate"
                            value={item.completionDate}
                            onChange={(e) => {
                                const data = [...education];
                                data[index].completionDate = e.target.value;
                                setEducation(data);
                            }}
                            max={new Date().toISOString().split("T")[0]}
                        />

                        {education.length > 1 && (
                            <div className="flex justify-end sm:justify-center md:justify-center">
                                <button
                                    type="button"
                                    onClick={() => removeEducation(index)}
                                    className="p-2 rounded hover:bg-red-100"
                                >
                                    <Trash2 size={16} className="text-red-500" />
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                <div className="mt-6 flex  sm:flex-row sm:justify-end gap-3">
                    <Button
                        type="button"
                        variant="danger"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="submit"
                        disabled={loading}
                        variant="success"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                {mode === "edit" ? "Updating..." : "Submitting..."}
                                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                            </span>
                        ) : (
                            mode === "edit" ? "Update Employee" : "Create Employee"
                        )}
                    </Button>
                </div>
                <div className="flex justify-end mt-[10px]">
                    {successMsg && (
                        <p className="mb-4 text-green-600 font-semibold">
                            {successMsg}
                        </p>
                    )}

                    {errorMsg && (
                        <p className="mb-4 text-red-600 font-semibold">
                            {errorMsg}
                        </p>
                    )}
                </div>
            </form>
        </div>
    )
}

export default CreateTeam;
